import React, { useMemo } from 'react';
import './ManagerKPIDashboard.css';
import KPICard from './KPICard';
import SkeletonBlock from './ui/SkeletonBlock';

function ManagerKPIDashboard({
  metrics = [],
  isLoading = false,
  error = '',
  title = 'Manager Performance Scorecard',
  badge = '',
  emptyMessage = 'No KPI data available.'
}) {
  const groupedMetrics = useMemo(() => {
    return metrics.reduce((categories, metric) => {
      const categoryName = metric.category || 'General';
      const sectionName = metric.sectionName || 'General';

      if (!categories[categoryName]) {
        categories[categoryName] = {};
      }

      if (!categories[categoryName][sectionName]) {
        categories[categoryName][sectionName] = [];
      }

      categories[categoryName][sectionName].push(metric);
      return categories;
    }, {});
  }, [metrics]);

  if (isLoading) {
    return <ManagerKpiSkeleton title={title} badge={badge} />;
  }

  if (error) {
    return <div className="manager-kpi-dashboard-error">{error}</div>;
  }

  if (!metrics.length) {
    return <div className="manager-kpi-dashboard-empty">{emptyMessage}</div>;
  }

  return (
    <div className="manager-kpi-dashboard">
      <div className="manager-kpi-cards-section">
        <div className="manager-kpi-section-header">
          <h2><i className="fas fa-gauge-high"></i> {title}</h2>
          <span className="manager-kpi-section-badge">{badge || `${metrics.length} Metrics`}</span>
        </div>

        <div className="manager-kpi-category-list">
          {Object.entries(groupedMetrics).map(([category, sections]) => (
            <div key={category} className="manager-kpi-category-block">
              <div className="manager-kpi-category-title-row">
                <h3 className="manager-kpi-category-title">{category}</h3>
              </div>

              {Object.entries(sections).map(([sectionName, sectionMetrics]) => (
                <div key={sectionName} className="manager-kpi-subgroup">
                  {sectionName !== 'General' && (
                    <div className="manager-kpi-subgroup-title">{sectionName}</div>
                  )}

                  <div className="manager-kpi-cards-grid">
                    {sectionMetrics.map(metric => (
                      <KPICard
                        key={metric.kpiKey}
                        title={metric.label}
                        actualValue={metric.actual}
                        goalValue={metric.goal}
                        unit={metric.unit}
                        isHigherBetter={metric.isHigherBetter}
                        variance={metric.variance}
                        percentToGoal={metric.percentToGoal}
                        rollingProgress={metric.rollingProgress}
                        elapsedPercent={metric.elapsedPercent}
                        status={metric.status}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ManagerKpiSkeleton({ title, badge }) {
  return (
    <div className="manager-kpi-dashboard" role="status" aria-live="polite" aria-label="Loading scorecard">
      <div className="manager-kpi-cards-section">
        <div className="manager-kpi-section-header">
          <h2><i className="fas fa-gauge-high"></i> {title}</h2>
          <span className="manager-kpi-section-badge">{badge || 'Loading'}</span>
        </div>
        <div className="manager-kpi-cards-grid">
          {Array.from({ length: 4 }).map((_, index) => (
            <div className="manager-kpi-skeleton-card" key={index}>
              <SkeletonBlock width="52%" height={14} />
              <SkeletonBlock width="78%" height={28} radius="var(--radius-10)" />
              <SkeletonBlock width="64%" height={12} />
              <SkeletonBlock width="100%" height={8} radius="var(--radius-pill)" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ManagerKPIDashboard;
